import type { SearchFilters, SearchParameters } from 'lib-search-types';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import { useCallback, useState } from 'react';
import { useEffectOnce, usePrevious, useUpdateEffect } from 'react-use';

import { customFieldsApi } from 'apis/customFields';
import { searchApi } from 'apis/search';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { searchActions } from '../../search.actions';
import { convertFiltersStringToObject } from '../filters/convertFilters';
import { enrichStaticFilters } from '../filters/enrichStaticFilters';
import {
  isCustomAttributeCondition,
  isLeafFilterCondition,
} from '../filters/filterTypeGuards';
import { convertSortStringToObject } from '../sort/convertSort';

type UseSearchInitResult = {
  isInitialized: boolean;
};

/** A hook that initializes the Search page. */
export function useSearchInit(): UseSearchInitResult {
  const dispatch = useAppDispatch();
  const { scope } = useAppPathParams('/search/:scope');
  const { params: urlParams } = useAppSearchParams('/search/:scope');
  const { params } = useAppSelector((state) => state.search);
  const prevParams = usePrevious(params);

  const [isInitialized, setIsInitialized] = useState(false);

  const [readSearchEnrichedFiltersQuery] =
    searchApi.useLazyReadSearchEnrichedFiltersQuery();

  const readCustomAttributes = useCallback(async () => {
    try {
      await dispatch(
        customFieldsApi.endpoints.readAttributes.initiate(
          { entityId: 'conversation' },
          { subscribe: true },
        ),
      ).unwrap();
    } catch (error) {}
  }, [dispatch]);

  const updateSearchParams = useCallback(
    (filters: SearchFilters<'conversations'>) => {
      const nextParams: SearchParameters<'conversations'> = {
        scope: scope as 'conversations',
        query: urlParams.q || '',
        filters,
        sortBy: convertSortStringToObject(urlParams.sort),
        isStrict: urlParams.strict === 'true' ? true : false,
        page: urlParams.page ? parseInt(urlParams.page) : 1,
      };

      dispatch(searchActions.params.update(nextParams));
    },
    [dispatch, scope, urlParams],
  );

  /**
   * Parses and enriches the filters from the URL params if necessary, then
   * updates the search Redux state with the initial params state.
   */
  const enrichAndUpdateParams = useCallback(async () => {
    const parsedFilters: SearchFilters<'conversations'> = enrichStaticFilters(
      convertFiltersStringToObject<'conversations'>(urlParams.filters),
    );

    try {
      const shouldEnrich = parsedFilters.filters.some(
        (condition) =>
          isLeafFilterCondition(condition) &&
          !isCustomAttributeCondition(condition) &&
          condition.values.some((value) => !value.label),
      );

      if (shouldEnrich) {
        const {
          data: { filters: enrichedFilters },
        } = await readSearchEnrichedFiltersQuery({
          scope: scope as 'conversations',
          filters: parsedFilters,
        }).unwrap();

        updateSearchParams(enrichedFilters as SearchFilters<'conversations'>);
      } else {
        updateSearchParams(parsedFilters);
      }

      // Even if the API request to enrich filters fails, we still fulfill with the partially parsed filters
      // It won't break the UI, but if any dynamic filters are set (e.g. agents or queues), their names won't be shown
    } catch (error) {
      updateSearchParams(parsedFilters);
    }
  }, [
    readSearchEnrichedFiltersQuery,
    scope,
    updateSearchParams,
    urlParams.filters,
  ]);

  const init = useCallback(async () => {
    try {
      // Fetch the full list of custom attributes so that components further down can access them to
      // display the attribute label or selected filter value labels.
      await readCustomAttributes();

      await enrichAndUpdateParams();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          urlParams,
        },
        tags: {
          team: SentryTeam.Insights,
        },
      });
    } finally {
      setIsInitialized(true);
    }
  }, [enrichAndUpdateParams, readCustomAttributes, urlParams]);

  useEffectOnce(() => {
    init();
  });

  // Handles re-initializing the Search state when the URL params change through navigating with the
  // browser's back/forward buttons. The check for params state equality is necessary because the
  // URL params can also change through a regular state update, which should not trigger a re-init.
  useUpdateEffect(() => {
    if (Object.is(params, prevParams)) {
      init();
    }
  }, [urlParams]);

  return { isInitialized: isInitialized };
}
